import { Check, Phone, MessageCircle, Calendar, MapPin, Plane, ShirtIcon, Sun, Heart, Video, Clock, CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';

export default function WelcomeEric() {
  const [daysUntilRetreat, setDaysUntilRetreat] = useState(0);
  const [daysUntilZoom, setDaysUntilZoom] = useState(0);

  useEffect(() => {
    const retreatDate = new Date('2026-07-23');
    const zoomDate = new Date('2026-07-09');
    const today = new Date();
    
    setDaysUntilRetreat(Math.ceil((retreatDate - today) / (1000 * 60 * 60 * 24)));
    setDaysUntilZoom(Math.ceil((zoomDate - today) / (1000 * 60 * 60 * 24)));
  }, []);

  const packingList = [
    { category: "Essentials", icon: Plane, items: ["Valid Passport (not expiring within 6 months)", "Travel insurance documents", "Any required medications", "Phone charger & adapter (Panama uses Type A/B plugs)"] },
    { category: "Clothing", icon: ShirtIcon, items: ["Comfortable casual clothes for sessions", "Swimwear for beach & pool", "Light jacket for air-conditioned rooms", "Workout/yoga attire", "One nice outfit for celebration dinner", "Comfortable walking shoes", "Sandals/flip-flops"] },
    { category: "Wellness", icon: Sun, items: ["Journal and pen for reflections", "Sunscreen (SPF 30+)", "Sunglasses & hat", "Personal toiletries", "Any supplements you take regularly"] },
    { category: "Mindset", icon: Heart, items: ["An open heart", "Willingness to be vulnerable", "Intention to connect with your brothers", "Readiness for transformation"] }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a2e] via-[#16213e] to-[#0f0f23]" data-testid="welcome-eric-page">
      
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="max-w-3xl mx-auto text-center relative z-10">
          {/* Personal Greeting */}
          <p className="text-emerald-400 font-sans uppercase tracking-[0.3em] text-sm font-semibold mb-6">
            A Personal Welcome
          </p>
          
          <h1 className="font-serif text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Eric, <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300">You Made It.</span>
          </h1>
          
          <p className="text-slate-300 font-sans text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Welcome to something special. You just made one of the most important decisions of your life — 
            <span className="text-emerald-400 font-semibold"> you chose yourself.</span>
          </p>

          {/* Countdown Cards */}
          <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
              <p className="text-5xl font-bold text-emerald-400 font-mono">{daysUntilRetreat}</p>
              <p className="text-slate-400 font-sans text-sm mt-2">Days Until Panama</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center">
              <p className="text-5xl font-bold text-white font-mono">{daysUntilZoom}</p>
              <p className="text-slate-400 font-sans text-sm mt-2">Days Until Zoom Call</p>
            </div>
          </div>
        </div>
      </section>

      {/* Affirmation Section */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-l-4 border-emerald-400 rounded-r-2xl p-8 md:p-10">
            <p className="text-slate-300 font-sans text-lg leading-relaxed mb-4">
              Eric, in a world that constantly asks you to pour into everyone else — your family, your job, your community — you made the radical choice to pour into <span className="text-white font-semibold">yourself</span>.
            </p>
            <p className="text-slate-300 font-sans text-lg leading-relaxed mb-4">
              That's not selfish. That's <span className="text-emerald-400 font-semibold">wisdom</span>.
            </p>
            <p className="text-slate-300 font-sans text-lg leading-relaxed mb-6">
              Because a man who is depleted cannot lead. A man who is burned out cannot be present. A man who neglects his own wellness cannot show up fully for those who need him.
            </p>
            <p className="text-white font-sans text-xl font-medium border-t border-white/10 pt-6">
              You chose to fill your cup so you can overflow into others. <br/>That takes courage. I see you.
            </p>
          </div>
        </div>
      </section>

      {/* Pre-Conference Zoom Call */}
      <section className="py-16 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <Video className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2">
              Let's Connect Before Panama
            </h2>
            <p className="text-slate-400 font-sans">A quick Zoom call to get you prepared and excited</p>
          </div>

          <div className="bg-[#1a1a2e] border border-emerald-500/30 rounded-2xl p-8 shadow-xl shadow-emerald-500/5">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">Thursday, July 9, 2026</p>
                    <p className="text-slate-500 font-sans text-sm">2 weeks before we meet</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">7:00 PM EST</p>
                    <p className="text-slate-500 font-sans text-sm">About 60 minutes</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <Video className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">Via Zoom</p>
                    <p className="text-slate-500 font-sans text-sm">Link coming to your email</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-6">
                <p className="text-emerald-400 font-sans text-sm uppercase tracking-wider font-semibold mb-4">What We'll Cover:</p>
                <ul className="space-y-3">
                  {["Meet your fellow travelers", "Travel logistics & what to expect", "Set your intentions for the retreat", "Answer all your questions"].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-slate-300 font-sans text-sm">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Bring */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2">
              What to Pack
            </h2>
            <p className="text-slate-400 font-sans">Your complete guide for Panama</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {packingList.map((section, index) => {
              const Icon = section.icon;
              return (
                <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-emerald-500/30 transition-colors">
                  <h3 className="text-emerald-400 font-sans font-semibold mb-4 flex items-center gap-3">
                    <Icon className="w-5 h-5" />
                    {section.category}
                  </h3>
                  <ul className="space-y-3">
                    {section.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                        <span className="text-slate-300 font-sans text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Travel Details */}
      <section className="py-16 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2">
              Travel Details
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Plane className="w-6 h-6 text-emerald-400" />
                <h3 className="text-white font-sans font-semibold">Arrival</h3>
              </div>
              <div className="space-y-2 text-slate-300 font-sans">
                <p><span className="text-slate-500">Date:</span> Thursday, July 23, 2026</p>
                <p><span className="text-slate-500">Airport:</span> Tocumen International (PTY)</p>
                <p><span className="text-slate-500">Arrive by:</span> 9:00 PM</p>
              </div>
              <p className="text-emerald-400 font-sans text-sm mt-4 flex items-center gap-2">
                <Check className="w-4 h-4" />
                Airport shuttle included — I'll pick you up!
              </p>
            </div>
            <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-6 h-6 text-emerald-400" />
                <h3 className="text-white font-sans font-semibold">Where We're Staying</h3>
              </div>
              <div className="space-y-2 text-slate-300 font-sans">
                <p><span className="text-slate-500">Hotel:</span> The Westin Playa Bonita</p>
                <p><span className="text-slate-500">Location:</span> Panama City, Panama</p>
                <p><span className="text-slate-500">Check-out:</span> Sunday, July 26 by 2:00 PM</p>
              </div>
              <a href="https://www.marriott.com/search/availabilityCalendar.mi?isRateCalendar=true&propertyCode=PTYBW" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="text-emerald-400 font-sans text-sm hover:underline mt-4 inline-block">
                View Hotel Details →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
            Got Questions?
          </h2>
          <p className="text-slate-400 font-sans mb-8">
            I'm here for you. Call or text me anytime — seriously.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="tel:804-310-7433" 
              className="flex items-center gap-3 bg-emerald-500 text-white px-8 py-4 rounded-full font-sans font-semibold hover:bg-emerald-600 transition-all hover:-translate-y-0.5 shadow-lg shadow-emerald-500/25"
              data-testid="call-btn"
            >
              <Phone className="w-5 h-5" />
              Call: (804) 310-7433
            </a>
            <a 
              href="sms:804-310-7433" 
              className="flex items-center gap-3 bg-transparent border-2 border-emerald-500 text-emerald-400 px-8 py-4 rounded-full font-sans font-semibold hover:bg-emerald-500/10 transition-all"
              data-testid="text-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>

          <p className="text-slate-500 font-sans text-sm mt-6">
            Or email: <a href="mailto:askcoachneil@gmail.com" className="text-emerald-400 hover:underline">askcoachneil@gmail.com</a>
          </p>
        </div>
      </section>

      {/* Final Message */}
      <section className="py-20 px-6 bg-gradient-to-t from-emerald-500/10 to-transparent">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <Heart className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
            See You Soon, Eric
          </h2>
          <p className="text-slate-300 font-sans text-lg leading-relaxed mb-8">
            I'm genuinely excited to meet you and walk this journey together. Get ready to be challenged, supported, and transformed.
          </p>
          <p className="text-emerald-400 font-serif text-2xl italic">
            "The best investment you can make is in yourself."
          </p>
          <p className="text-slate-500 font-sans text-sm mt-6">
            — Looking forward to Panama 🌴
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-600 font-sans text-sm">
            July 23-26, 2026 · Panama City, Panama · The Westin Playa Bonita
          </p>
        </div>
      </footer>
    </div>
  );
}
